// ============================================
// Time Constants (in milliseconds)
// ============================================

const ONE_SECOND = 1000;
const ONE_MINUTE = 60 * 1000;
export const FIVE_MINUTES = 5 * 60 * 1000;
const THIRTY_MINUTES = 30 * 60 * 1000;
export const ONE_HOUR = 60 * 60 * 1000;
export const TWENTY_FOUR_HOURS = 24 * 60 * 60 * 1000;
export const SEVEN_DAYS = 7 * 24 * 60 * 60 * 1000;

// ============================================
// Cache TTL Constants (in milliseconds)
// ============================================

export const CACHE_TTL = {
  DASHBOARD_KPIS_MS: 30 * ONE_SECOND,
  DASHBOARD_SUMMARY_MS: 45 * ONE_SECOND,
  ADS_QUALITY_SCORE_MS: ONE_MINUTE,
  ADS_QUALITY_SPEND_BY_AD_MS: ONE_MINUTE,
  ADS_QUALITY_SCORE_BUCKET_MS: ONE_MINUTE,
  ATTRIBUTION_AD_COUNTS_MS: ONE_MINUTE,
} as const;

// ============================================
// Pagination + Batch Size Constants
// ============================================

export const PAGINATION = {
  DASHBOARD_CONVERSATION_ID_PAGE_SIZE: 1000,
  ATTRIBUTION_LEADS_DEFAULT_LIMIT: 200,
  ATTRIBUTION_LEADS_MAX_LIMIT: 500,
  ATTRIBUTION_BATCH_IN_FETCH_SIZE: 500,
  MANUAL_ATTRIBUTION_CONVERSATION_PAGE_SIZE: 1000,
  MANUAL_ATTRIBUTION_CONVERSATION_MAX_ROWS: 10000,
  MANUAL_ATTRIBUTION_DATE_BACKFILL_BATCH_SIZE: 50,
  CONTACT_MATCHING_CONVERSATION_BATCH_SIZE: 500,
  CONTACT_MATCHING_CONVERSATION_MAX_ROWS: 10000,
  CONTACT_MATCHING_DB_WRITE_CHUNK_SIZE: 50,
} as const;

// ============================================
// Token Refresh Thresholds
// ============================================

/** Refresh Close tokens when expiring within this window (tokens last 1 hour) */
export const CLOSE_REFRESH_THRESHOLD = THIRTY_MINUTES;

/** Minimum acceptable token expiry (5 minutes) - reject tokens with shorter expiry */
export const MIN_TOKEN_EXPIRY_SECONDS = 300;

// ============================================
// Meta/Facebook/Instagram API Constants
// ============================================

/** Current Meta Graph API version used across all endpoints */
export const META_GRAPH_API_VERSION = "v24.0";

// ============================================
// Facebook Login OAuth Permissions
// ============================================

/** All Facebook Login permissions as an array */
export const FB_LOGIN_PERMISSIONS_LIST = [
  "instagram_basic",
  "instagram_manage_messages",
  "pages_show_list",
  "pages_read_engagement",
  "pages_read_user_content",
  "pages_manage_engagement",
  "pages_manage_metadata",
  "pages_messaging",
  "ads_read",
  "business_management",
] as const;

/** All Facebook Login permissions as a comma-separated string (for OAuth scope param) */
export const FB_LOGIN_SCOPE = FB_LOGIN_PERMISSIONS_LIST.join(",");

// ============================================
// WebSocket Constants
// ============================================

/**
 * WebSocket server configuration (Bun.serve websocket options)
 * Reference: https://bun.sh/docs/api/websockets
 */
export const WS_IDLE_TIMEOUT_SECONDS = 300; // 5 minutes (default 120s)
export const WS_MAX_PAYLOAD_LENGTH = 64 * 1024; // 64KB - chat messages are small
export const WS_BACKPRESSURE_LIMIT = 256 * 1024; // 256KB buffer before backpressure

/**
 * WebSocket client heartbeat configuration
 * Client sends ping, server responds with pong
 */
export const WS_HEARTBEAT_INTERVAL_MS = 30_000; // Send ping every 30 seconds
export const WS_HEARTBEAT_TIMEOUT_MS = 10_000; // Wait 10s for pong before reconnect

/**
 * WebSocket client reconnection configuration
 * Exponential backoff with jitter
 */
export const WS_RECONNECT_BASE_DELAY_MS = 1_000; // Start with 1 second
export const WS_RECONNECT_MAX_DELAY_MS = 30_000; // Cap at 30 seconds

/**
 * WebSocket close codes (application-defined: 4000-4999)
 * Reference: https://developer.mozilla.org/en-US/docs/Web/API/CloseEvent/code
 */
export const WS_CLOSE_HEARTBEAT_TIMEOUT = 4000;
export const WS_CLOSE_AUTH_REQUIRED = 4001;
export const WS_CLOSE_AUTH_INVALID = 4002;

// ============================================
// Feature Flags
// ============================================

export const FEATURES = {
  /** Store contract PDF URLs in esignatures table */
  ESIGNATURES_PDF_URL: true,
  /** Show HubSpot integration card on Integrations page */
  HUBSPOT_INTEGRATION: false,
  /**
   * Enable cron jobs and background services.
   * True in production or forced on in non-production via ENABLE_CRONS=true.
   * Runtime process-group gating still decides whether this process should run crons.
   * Getter so tests can override process.env at runtime.
   */
  get CRONS_ENABLED(): boolean {
    return process.env.NODE_ENV === "production" || process.env.ENABLE_CRONS === "true";
  },
  /**
   * Allow this process to create/update Meta webhook page subscriptions.
   * Must be explicitly enabled via ENABLE_META_WEBHOOK_SUBSCRIPTIONS=true.
   * Environments that only consume forwarded webhooks (e.g. staging) should leave this unset.
   */
  get META_WEBHOOK_SUBSCRIPTIONS(): boolean {
    return process.env.ENABLE_META_WEBHOOK_SUBSCRIPTIONS === "true";
  },
} as const satisfies Record<string, boolean>;
